/** @jsx jsx */
import React, { useCallback } from 'react';
import { css, styled, jsx } from '@compiled/react';
import { Bleed, Box, Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import AddIcon from '@atlaskit/icon/core/add';
import { cssMap } from '@atlaskit/css';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { SuspenseMigrationPlaceholder } from '@atlassian/jira-placeholder/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import messages from '../../../messages.tsx';
import { useTeamPermissions } from '../../../services/use-team-permissions/index.tsx';

const styles = cssMap({
	iconWrapper: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		paddingTop: token('space.075'),
		paddingRight: token('space.075'),
		paddingBottom: token('space.075'),
		paddingLeft: token('space.075'),
	},
	optionWrapper: {
		cursor: 'pointer',
	},
});

type CreateTeamOptionProps = {
	onClick: () => void;
	isFocused: boolean;
	setIsFooterFocused?: React.Dispatch<React.SetStateAction<boolean>>;
};

export function CreateTeamOption({
	onClick,
	isFocused,
	setIsFooterFocused = () => null,
}: CreateTeamOptionProps) {
	const { formatMessage } = useIntl();
	const formattedCreateTeamText = formatMessage(messages.createTeamButtonText);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleCreateTeamOptionClick = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'createTeamInline', {
			teamsFieldEmptyState: false,
		});
		onClick();
	}, [onClick, createAnalyticsEvent]);

	const setIsFooterFocusedWithFG = fg('a11y-create-team-is-not-focusable-and-has-no-btn')
		? setIsFooterFocused
		: () => null;

	const handleEscapeOrTabClick = (event: React.KeyboardEvent) => {
		if (event.key === 'Escape' || event.key === 'Tab') {
			setIsFooterFocusedWithFG(false);
		}
	};

	const { canCreateTeams } = useTeamPermissions();

	return canCreateTeams ? (
		<SuspenseMigrationPlaceholder
			fallback={null}
			name="CreateTeamOption"
			condition={fg('jfp-magma-bulk-migrate-to-placeholder')}
		>
			<Bleed inline="space.200" block="space.100">
				<CreateButtonBorder />
				<Container
					onClick={handleCreateTeamOptionClick}
					isFocused={isFocused}
					data-testid="issue-field-team.ui.edit-mode.create-team-option.create-team-inline-option"
				>
					{fg('a11y-create-team-is-not-focusable-and-has-no-btn') ? (
						<button
							css={wrapperButtonStyles}
							onFocus={() => setIsFooterFocusedWithFG(true)}
							onBlur={() => setIsFooterFocusedWithFG(false)}
							onKeyDown={handleEscapeOrTabClick}
						>
							<OptionDataWrapper>
								<Inline xcss={styles.optionWrapper} space="space.100" alignBlock="center">
									<Box xcss={styles.iconWrapper}>
										<AddIcon label="" spacing="spacious" />
									</Box>
									{formattedCreateTeamText}
								</Inline>
							</OptionDataWrapper>
						</button>
					) : (
						<OptionDataWrapper>
							<Inline xcss={styles.optionWrapper} space="space.100" alignBlock="center">
								<Box xcss={styles.iconWrapper}>
									<AddIcon label="" spacing="spacious" />
								</Box>
								{formattedCreateTeamText}
							</Inline>
						</OptionDataWrapper>
					)}
				</Container>
			</Bleed>
		</SuspenseMigrationPlaceholder>
	) : null;
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CreateButtonBorder = styled.div({
	borderTop: `1px solid ${token('color.border')}`,
});

const wrapperButtonStyles = css({
	border: 'none',
	width: '100%',
	height: '100%',
	background: 'none',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div<{ isFocused: boolean }>({
	marginTop: token('space.025'),
	width: '100%',
	height: '100%',
	'&:hover': {
		backgroundColor: token('color.background.neutral.subtle.hovered'),
		boxShadow: `inset 2px 0px 0px ${token('color.border.selected')}`,
	},
	// isFocused
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: ({ isFocused }) =>
		isFocused ? token('color.background.neutral.subtle.hovered') : 'transparent',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	boxShadow: ({ isFocused }) =>
		isFocused ? `inset 2px 0px 0px ${token('color.border.selected')}` : 'none',
});

// Matching spacing of other options
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const OptionDataWrapper = styled.div({
	paddingTop: token('space.075'),
	paddingRight: token('space.150'),
	paddingBottom: token('space.075'),
	paddingLeft: token('space.150'),
});
